import { hybridStorage } from './HybridStorage';
import { syncQueue } from './SyncQueue';
import { networkManager } from './NetworkManager';
import { useState, useEffect } from 'react';

export interface HybridSystemStatus {
  isInitialized: boolean;
  isOnline: boolean;
  storage: {
    localStorage: number;
    sqlite: number;
    pendingSync: number;
  };
  syncQueue: {
    pending: number;
    processing: boolean;
    lastSync: string | null;
  };
}

export class HybridManager {
  private static instance: HybridManager;
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;

  private constructor() {}

  static getInstance(): HybridManager {
    if (!HybridManager.instance) {
      HybridManager.instance = new HybridManager();
    }
    return HybridManager.instance;
  }

  async initialize(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      console.log('🚀 Initializing hybrid storage system...');

      // Initialize core services
      await hybridStorage.initialize();
      console.log('✅ Hybrid storage ready');

      // NetworkManager doesn't need initialization
      console.log('✅ Network manager ready');

      // syncQueue initializes automatically
      console.log('✅ Sync queue ready');

      this.isInitialized = true;
      console.log('🎉 Hybrid system fully initialized!');

    } catch (error) {
      console.error('❌ Failed to initialize hybrid system:', error);
      this.initPromise = null; // Allow retry
      throw error;
    }
  }

  async getSystemStatus(): Promise<HybridSystemStatus> {
    if (!this.isInitialized) {
      throw new Error('Hybrid system not initialized. Call initialize() first.');
    }

    try {
      const [storageStats, pendingItems] = await Promise.all([
        hybridStorage.getStorageStats(),
        hybridStorage.getAllPendingSync()
      ]);

      const pendingCount = Object.values(pendingItems).reduce(
        (total, items) => total + items.length, 
        0
      );

      return {
        isInitialized: this.isInitialized,
        isOnline: networkManager.getConnectionStatus(),
        storage: {
          localStorage: storageStats.total.localStorageItems,
          sqlite: storageStats.total.sqliteRows,
          pendingSync: pendingCount
        },
        syncQueue: {
          pending: pendingCount,
          processing: false, // We'd need to add this to SyncQueue
          lastSync: null // We'd need to track this
        }
      };
    } catch (error) {
      console.error('Error getting system status:', error);
      throw error;
    }
  }

  async clearAllData(): Promise<void> {
    if (!this.isInitialized) {
      throw new Error('Hybrid system not initialized');
    }

    try {
      console.log('🧹 Clearing all hybrid storage data...');
      await hybridStorage.clearCache();
      console.log('✅ All data cleared');
    } catch (error) {
      console.error('❌ Error clearing data:', error);
      throw error;
    }
  }

  async forceSync(): Promise<void> {
    if (!this.isInitialized) {
      throw new Error('Hybrid system not initialized');
    }

    if (!networkManager.getConnectionStatus()) {
      throw new Error('Cannot sync while offline');
    }

    try {
      console.log('🔄 Forcing sync of all pending data...');
      await syncQueue.processPendingItems();
      console.log('✅ Sync completed');
    } catch (error) {
      console.error('❌ Error during forced sync:', error);
      throw error;
    }
  }

  async exportDebugData(): Promise<any> {
    try {
      const [status, storageStats] = await Promise.all([
        this.getSystemStatus(),
        hybridStorage.getStorageStats()
      ]);

      return {
        timestamp: new Date().toISOString(),
        system: status,
        storage: storageStats,
        platform: {
          isWeb: typeof window !== 'undefined',
          isNative: typeof window === 'undefined'
        }
      };
    } catch (error) {
      console.error('Error exporting debug data:', error);
      return { error: error instanceof Error ? error.message : String(error) };
    }
  }

  // Getters for convenience
  get isReady(): boolean {
    return this.isInitialized;
  }

  get storage() {
    return hybridStorage;
  }

  get network() {
    return networkManager;
  }

  get sync() {
    return syncQueue;
  }
}

// React hooks for UI integration
export function useHybridStatus() {
  const [status, setStatus] = useState<HybridSystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const newStatus = await hybridManager.getSystemStatus();
      setStatus(newStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshStatus();
    
    // Refresh status every 30 seconds
    const interval = setInterval(refreshStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  return {
    status,
    loading,
    error,
    refresh: refreshStatus,
    clearData: () => hybridManager.clearAllData(),
    forceSync: () => hybridManager.forceSync()
  };
}

export function useHybridInitialization() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialize = async () => {
    try {
      setIsInitializing(true);
      setError(null);
      await hybridManager.initialize();
      setIsInitialized(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    if (!isInitialized && !isInitializing) {
      initialize();
    }
  }, []);

  return {
    isInitialized,
    isInitializing,
    error,
    retry: initialize
  };
}

// Simple network status hook
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    const updateStatus = async () => {
      setIsOnline(networkManager.getConnectionStatus());
    };

    // Update immediately
    updateStatus();

    // Listen for changes
    networkManager.addSyncCallback(updateStatus);

    // Cleanup would need to be implemented in NetworkManager
    return () => {
      // networkManager.removeSyncCallback(updateStatus);
    };
  }, []);

  return { isOnline };
}

// Export singleton instance
export const hybridManager = HybridManager.getInstance();